import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ResponsiveTable from '~/components/VisionUI/ResponsiveTable';
import SearchBar from '~/components/VisionUI/SearchBar';
import TableRowsLoader from '~/components/VisionUI/TableRowsLoader';

import SSHKeyRows from './components/SSHKeyRows';
import SSHEmptyKey from './components/SSHKeysEmptyCard';
import SSHMobileKeyCard from './components/SSHMobileKeyCard';
import { getSSHKeyColumns } from './utils/helper';
import { SSHKey } from './utils/types';

interface SSHKeysTableProps {
  sshKeys: SSHKey[];
  isLoading: boolean;
  reAddingKeyId?: number | null;

  onView: (id: number) => void;
  onDelete: (id: number) => void;
  onReAdd?: (id: number) => void;
  onRemove?: (id: number) => void;

  testId?: string;
  headerTestId?: string;
  bannerTestId?: string;
  searchInputTestId?: string;

  icon?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;

  searchQuery?: string;
  onSearch?: (value: string) => void;
  onCancelSearch?: () => void;
  onAddAction?: () => void;

  actions?: ReactNode;
  banner?: ReactNode;
  pagination?: ReactNode;

  hasItems?: boolean;
  hasSearchResults: boolean;
  showPaginationWhenNoSearchResults: boolean;
  showStatusColumn?: boolean;
  showTypeColumn?: boolean;
  serverStatus?: string;
  isVps?: boolean;
  isRefreshing?: boolean;
}

const cardSx = {
  width: '100%',
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 3,
  bgcolor: 'common.white',
};

const headerSx = {
  minHeight: 84,
  px: 2.5,
  py: 2,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const actionControlsSx = {
  width: { xs: '100%', lg: 'auto' },
  flexWrap: { xs: 'wrap', lg: 'nowrap' },
  justifyContent: { xs: 'flex-start', lg: 'flex-end' },
  gap: 1.5,
};

const SSHKeysTable = ({
  sshKeys,
  isLoading,
  onView,
  onDelete,
  onReAdd = undefined,
  onRemove = undefined,
  testId = '',
  headerTestId = '',
  bannerTestId = '',
  searchInputTestId = '',
  icon = null,
  title = null,
  description = null,
  searchQuery = '',
  onSearch = () => {},
  onCancelSearch = undefined,
  onAddAction = () => {},
  actions = null,
  banner = null,
  pagination = null,
  hasItems = sshKeys.length > 0,
  hasSearchResults,
  showPaginationWhenNoSearchResults,
  showStatusColumn = false,
  showTypeColumn = true,
  reAddingKeyId = null,
  serverStatus = '',
  isVps = false,
  isRefreshing = false,
}: SSHKeysTableProps) => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'sshSettings',
  });

  const hasSearchEmptyState = hasItems && !hasSearchResults;

  const showPagination =
    hasItems && (hasSearchResults || showPaginationWhenNoSearchResults);
  const isServerOnline = !isVps || !serverStatus || serverStatus === 'active';

  const columnCount = 3 + Number(showStatusColumn) + Number(showTypeColumn);

  const content =
    !hasItems && !isLoading ? (
      <SSHEmptyKey
        heading={t('emptyState.heading')}
        description={t('emptyState.description')}
        buttonLabel={t('emptyState.createBtn')}
        onAdd={onAddAction}
        disabled={!isServerOnline || isRefreshing}
      />
    ) : hasSearchEmptyState ? (
      <Stack alignItems="center" justifyContent="center" sx={{ px: 3, py: 6 }}>
        <Typography align="center" variant="h3" color="text.secondary">
          {t('emptySearchResults', {
            searchTerm: searchQuery,
          })}
        </Typography>
      </Stack>
    ) : (
      <ResponsiveTable
        type="ssh-keys"
        list={sshKeys}
        headCells={getSSHKeyColumns(t, showStatusColumn, showTypeColumn)}
        isPending={isLoading}
        loader={<TableRowsLoader pageSize={3} columns={columnCount} />}
        showPagination={false}
        mobile={{
          ui: (
            <SSHMobileKeyCard
              sshKeys={sshKeys}
              reAddingKeyId={reAddingKeyId}
              onView={onView}
              onDelete={onDelete}
              onReAdd={onReAdd}
              onRemove={onRemove}
              showStatusColumn={showStatusColumn}
              showTypeColumn={showTypeColumn}
              isServerOnline={isServerOnline && !isRefreshing}
            />
          ),
        }}
      >
        <SSHKeyRows
          showStatusColumn={showStatusColumn}
          showTypeColumn={showTypeColumn}
          reAddingKeyId={reAddingKeyId}
          list={sshKeys}
          onView={onView}
          onDelete={onDelete}
          onReAdd={onReAdd}
          onRemove={onRemove}
          isServerOnline={isServerOnline && !isRefreshing}
        />
      </ResponsiveTable>
    );

  return (
    <Stack data-testid={testId} sx={cardSx} spacing={{ xs: 1, md: 0 }}>
      <Stack
        data-testid={headerTestId}
        direction={{ xs: 'column', lg: 'row' }}
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 2, lg: 0 }}
        sx={headerSx}
      >
        <Stack direction="row" alignItems="center" spacing={1.2}>
          {icon}

          <Stack spacing={0.2}>
            <Typography variant="h3">{title}</Typography>

            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" sx={actionControlsSx}>
          {hasItems && (
            <SearchBar
              searchQuery={searchQuery}
              handleSearch={onSearch}
              cancelSearch={onCancelSearch}
              placeholder={t('placeholder')}
              testId={searchInputTestId}
            />
          )}

          {actions}
        </Stack>
      </Stack>

      {banner && <Stack data-testid={bannerTestId}>{banner}</Stack>}

      {content}

      {showPagination && pagination}
    </Stack>
  );
};

export type { SSHKey, SSHKeysTableProps };
export default SSHKeysTable;
